/* eslint-disable react-hooks/exhaustive-deps */
import { Button, ButtonPrimary } from '@/components/button'
import { PlayerInfoSection } from '@/components/common'
import { ArrowBack, ChevronRight, People, Person, SearchNoBg } from '@/components/icons'
import { Search } from '@/components/search'
import { PlayerItemLoading } from '@/components/skeleton'
import { LIMIT, OFFSET } from '@/constants/Common'
import { ROUTE_CLAIM, ROUTE_DASHBOARD, ROUTE_SIGNUP } from '@/constants/Routes'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { userSelector } from '@/pages/user/selectors'
import { receiveSearchTextClaimAccount } from '@/pages/user/userSlice'
import { useGetClaimUsers } from '@/services'
import { Player } from '@/types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

export const Claim = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { token } = useAppSelector(authSelector)
  const { searchTextClaimAccount } = useAppSelector(userSelector)

  const [offset, setOffset] = useState(OFFSET)
  const [players, setPlayers] = useState<Map<number, Player>>(new Map())

  const debouncedSearchText = useDebounce<string>(searchTextClaimAccount || '', 200)

  const {
    data: resClaimUser,
    isLoading,
    isFetching,
  } = useGetClaimUsers({
    limit: LIMIT,
    offset: offset,
    query: debouncedSearchText,
  })

  useEffect(() => {
    if (isFetching) {
      if (offset === OFFSET) {
        setPlayers(new Map())
      }
    }
  }, [isFetching])

  const onGoBack = () => navigate(-1)

  const onNavigateSignUp = () => navigate(ROUTE_SIGNUP)

  const onChangeSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(receiveSearchTextClaimAccount(e.target.value))
    setOffset(OFFSET)
  }

  const handleScroll = (event: any) => {
    const { target } = event
    if (target.scrollHeight - target.scrollTop === target.clientHeight && resClaimUser?.hasMore) {
      setOffset((pre) => pre + LIMIT)
    }
  }

  const onNavigateClaimDetail = (claim: Player) => {
    navigate(`${ROUTE_CLAIM}/${claim.referralCode}`, {
      state: {
        claimDetail: claim,
        claimId: claim.referralCode,
      },
    })
  }

  useEffect(() => {
    if (resClaimUser) {
      setPlayers((old) => {
        let temp: Map<number, Player>
        if (offset === OFFSET) {
          temp = new Map()
        } else {
          temp = old
        }

        resClaimUser.hits.forEach((player) => {
          temp.set(player.id, player)
        })

        return temp
      })
    }
  }, [isFetching])

  useEffect(() => {
    if (token) {
      navigate(ROUTE_DASHBOARD, { relative: 'path', replace: true })
    }
  }, [token])

  return (
    <>
      <div className="w-screen relative h-screen pt-4 sm:pt-24 overflow-hidden flex justify-center">
        <div className="w-full px-4 sm:w-[460px] md:w-[520px] lg:w-[640px] flex flex-col gap-y-7 pb-28 xs:pb-24">
          <Link className="text-white font-bold self-end flex" to={ROUTE_SIGNUP}>
            Create Account <ChevronRight />
          </Link>
          <div className="flex flex-col gap-y-7">
            <div className="flex relative flex-col gap-y-4 sm:flex-row sm:items-center mt-8 sm:mt-0">
              <Button onClick={onGoBack} className="hover:opacity-50 flex sm:absolute sm:-left-12 sm:top-1">
                <ArrowBack className="text-white" />
              </Button>
              <div className="flex flex-col gap-y-[10px]">
                <h1 className="text-2xl xs:text-3xl font-bold text-white">{t('auth.claimAccount')}</h1>
                <p className="text-sm text-white">{t('auth.claimAccountDesc')}</p>
              </div>
            </div>
            <Search
              searchText={searchTextClaimAccount}
              onChangeInput={onChangeSearchInput}
              className="p-4 w-full gap-x-2"
            />
          </div>
          {debouncedSearchText && (
            <div className="flex flex-col gap-y-2 overflow-y-auto overflow-x-hidden" onScroll={(e) => handleScroll(e)}>
              {Array.from(players.values())?.map((item) => (
                <div
                  className="flex justify-between bg-white rounded-2xl p-4 cursor-pointer"
                  key={item.id}
                  onClick={() => onNavigateClaimDetail(item)}
                >
                  <PlayerInfoSection player={item} />
                  <div className="flex gap-x-1 items-center">
                    <div className="bg-navy-900 bg-opacity-10 rounded-lg px-2 py-1 w-11 h-10 flex flex-col justify-center items-center">
                      <People className="text-navy-900" />
                      <span className="text-xs text-navy-900 font-medium">{item?.ratings?.doubles}</span>
                    </div>
                    <div className="bg-navy-900 bg-opacity-10 rounded-lg px-2 py-1 w-11 h-10 flex flex-col justify-center items-center">
                      <Person className="text-navy-900" />
                      <span className="text-xs text-navy-900 font-medium">{item?.ratings?.singles}</span>
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <>
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                  <PlayerItemLoading />
                </>
              )}
              {resClaimUser?.empty && (
                <div className="flex justify-center">
                  <p className="text-slate-300 text-base font-semibold">{t('common.noMoreResults')}</p>
                </div>
              )}
            </div>
          )}
          {!debouncedSearchText && (
            <div className="flex flex-col items-center gap-y-4">
              <SearchNoBg className="w-[114px] h-[114px] text-white text-opacity-30" />
              <p className="text-sm text-white text-opacity-50 text-center max-w-[265px]">
                {t('auth.claimAccountHelpText')}
              </p>
            </div>
          )}
        </div>
        {debouncedSearchText && (
          <div className="fixed bottom-0 right-0 bg-white w-full flex items-center justify-center">
            <div className="flex flex-col xs:flex-row items-center gap-y-2 xs:gap-x-4 pb-4 pt-4 sm:pb-6">
              <p className="text-sm text-navy-900">{t('auth.notSeeYourself')}</p>
              <ButtonPrimary
                title={t('auth.createAccount')}
                className="!px-5 !py-2 xs:!py-3"
                onClick={onNavigateSignUp}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}
